import { useEffect, useRef } from 'react';
import { ChevronFirst, ChevronLast, ChevronLeft, ChevronRight, FlipVertical2 } from 'lucide-react';
import { useT } from '../app/preferences.ts';

interface MoveListProps {
  sans: readonly string[];
  /** Ply being shown: 0 = start position, sans.length = live position. */
  ply: number;
  onSelect: (ply: number) => void;
  onFlip: () => void;
}

export function MoveList({ sans, ply, onSelect, onFlip }: MoveListProps) {
  const t = useT();
  const listRef = useRef<HTMLDivElement>(null);
  const last = sans.length;

  // Keep the current move in view.
  useEffect(() => {
    const el = listRef.current?.querySelector<HTMLElement>('.move-btn.current');
    if (el) el.scrollIntoView({ block: 'nearest' });
    else if (ply === last && listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [ply, last]);

  // Arrow-key navigation, ignored while typing in a field.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      if (target?.closest('input, textarea, select, dialog[open]')) return;
      if (e.key === 'ArrowLeft') onSelect(Math.max(0, ply - 1));
      else if (e.key === 'ArrowRight') onSelect(Math.min(last, ply + 1));
      else if (e.key === 'ArrowUp' || e.key === 'Home') onSelect(0);
      else if (e.key === 'ArrowDown' || e.key === 'End') onSelect(last);
      else return;
      e.preventDefault();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [ply, last, onSelect]);

  const rows: { no: number; white?: number; black?: number }[] = [];
  for (let i = 0; i < sans.length; i += 2) rows.push({ no: i / 2 + 1, white: i, black: i + 1 < sans.length ? i + 1 : undefined });

  const cell = (index: number | undefined) =>
    index === undefined ? (
      <span />
    ) : (
      <button
        type="button"
        className={`move-btn${ply === index + 1 ? ' current' : ''}`}
        aria-current={ply === index + 1 ? 'step' : undefined}
        onClick={() => onSelect(index + 1)}
      >
        {sans[index]}
      </button>
    );

  return (
    <>
      <div className="move-list" ref={listRef} aria-label={t('game.moves')}>
        {rows.length === 0 ? (
          <div className="move-list-empty">{t('game.noMoves')}</div>
        ) : (
          rows.map((r) => (
            <div key={r.no} className="move-row">
              <span className="move-no">{r.no}.</span>
              {cell(r.white)}
              {cell(r.black)}
            </div>
          ))
        )}
      </div>
      <div className="history-nav">
        <button type="button" className="icon-btn" onClick={onFlip} aria-label={t('game.flip')} title={t('game.flip')}>
          <FlipVertical2 size={24} />
        </button>
        <button
          type="button"
          className="icon-btn"
          onClick={() => onSelect(0)}
          disabled={ply === 0}
          aria-label={t('game.history.first')}
          title={t('game.history.first')}
        >
          <ChevronFirst size={26} />
        </button>
        <button
          type="button"
          className="icon-btn"
          onClick={() => onSelect(ply - 1)}
          disabled={ply === 0}
          aria-label={t('game.history.prev')}
          title={t('game.history.prev')}
        >
          <ChevronLeft size={26} />
        </button>
        <button
          type="button"
          className="icon-btn"
          onClick={() => onSelect(ply + 1)}
          disabled={ply === last}
          aria-label={t('game.history.next')}
          title={t('game.history.next')}
        >
          <ChevronRight size={26} />
        </button>
        <button
          type="button"
          className="icon-btn"
          onClick={() => onSelect(last)}
          disabled={ply === last}
          aria-label={t('game.history.last')}
          title={t('game.history.last')}
        >
          <ChevronLast size={26} />
        </button>
      </div>
    </>
  );
}
